import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEqual, isEmpty } from 'lodash';
import classNames from 'classnames';
import { InputGroup, FormControl, ControlLabel } from 'react-bootstrap';

import { PasswordStrengthIndicator } from '@components/common';
import utils from '@utils';

class TextInput extends Component {
	constructor(props) {
		super(props);
		const { options : { showPasswordStrength }, value } = props;

		this.debounceTimeout = null;
		this.debounceFunction = null;
		this.state = {
			value         : props.value,
			// eslint-disable-next-line no-nested-ternary
			passwordScore : value && showPasswordStrength
				? (value.length < 8
					? 5 : utils.calculatePasswordScore(value))
				: 5,
		};
	}

	UNSAFE_componentWillReceiveProps = ({ value : nextValue }) => {
		const { value } = this.state;
		if (!isEqual(nextValue, value)) this.setState({ value : nextValue });
	}

	shouldComponentUpdate = ({ rawErrors }, { value, passwordScore }) => (
		/* eslint-disable */
		!isEqual(value, this.state.value)
		|| !isEqual(passwordScore, this.state.passwordScore)
		|| !isEqual(rawErrors, this.props.rawErrors)
		/* eslint-enable */
	)

	onChange = ({ target : { value } }) => {
		const { options : { showPasswordStrength, avoidDebounce }, onChange } = this.props;
		this.setState({ value });
		if (showPasswordStrength) {
			this.setState({
				passwordScore : value.length >= 8 ? utils.calculatePasswordScore(value) : 5,
			});
		}

		if (avoidDebounce) {
			onChange(value);
			return;
		}
		/**
		 * Clear debounceTimeout to prevent updating the Store
		 * multiple times using the JS native function
		 * clearTimeout
		 */
		if (this.debounceTimeout !== null) clearTimeout(this.debounceTimeout);

		this.debounceFunction = () => onChange(value);
		this.debounceTimeout = setTimeout(this.debounceFunction, 300);
	}

	render() {
		const {
			placeholder,
			options : {
				label,
				name,
				wrapperClassName,
				icon,
				type,
				inputClassName,
				inputGroupClassName,
				labelWrapperClassName,
				showPasswordStrength,
				large,
				componentClass,
				textAreaRows,
				labelSubText,
				labelFont,
				labelFontSize,
				disabled,
				labelAddition,
			},
			rawErrors,
		} = this.props;
		const { value, passwordScore } = this.state;

		return (
			<div className={wrapperClassName}>
				<div className={classNames(labelWrapperClassName, { flex : labelSubText })}>
					<ControlLabel className={classNames(`${labelFont} ${labelFontSize} color-gray-mine-shaft`,
						{
							'color-pink-brink' : !isEmpty(rawErrors),
							'flex-2'           : labelSubText,
						})}
					>
						{ label }
						<If condition={labelAddition}>
							<small className="font-size-10 margin-left-5">{ labelAddition }</small>
						</If>
					</ControlLabel>
					<If condition={labelSubText}>
						<span className="font-size-12 padding-top-3 font-regular-italic">{ labelSubText }</span>
					</If>
				</div>
				<InputGroup
					bsSize={large ? 'large' : undefined}
					className={inputGroupClassName}
				>
					<If condition={!isEmpty(icon)}>
						<InputGroup.Addon className={classNames({ 'input-error' : !isEmpty(rawErrors) })}>
							<i className={classNames(`rkf rkf-${icon}`, { 'color-pink-brink' : !isEmpty(rawErrors) })} />
						</InputGroup.Addon>
					</If>
					<FormControl
						type={type}
						name={name}
						componentClass={componentClass}
						rows={textAreaRows}
						value={value}
						placeholder={placeholder}
						onChange={this.onChange}
						className={classNames(`font-size-14 color-blue-bayoux font-regular bg-white ${inputClassName}`,
							{ 'input-error' : !isEmpty(rawErrors) && !showPasswordStrength })}
						disabled={disabled}
					/>
				</InputGroup>
				<If condition={showPasswordStrength}>
					<PasswordStrengthIndicator passwordScore={passwordScore} />
				</If>
			</div>
		);
	}
}

TextInput.propTypes = {
	onChange    : PropTypes.func.isRequired,
	value       : PropTypes.string,
	placeholder : PropTypes.string,
	rawErrors   : PropTypes.array,
	options     : PropTypes.shape({
		name                  : PropTypes.string,
		showPasswordStrength  : PropTypes.bool,
		label                 : PropTypes.string,
		wrapperClassName      : PropTypes.string,
		inputClassName        : PropTypes.string,
		inputGroupClassName   : PropTypes.string,
		labelWrapperClassName : PropTypes.string,
		icon                  : PropTypes.string,
		type                  : PropTypes.string,
		componentClass        : PropTypes.string,
		textAreaRows          : PropTypes.number,
		labelSubText          : PropTypes.string,
		large                 : PropTypes.bool,
		labelFont             : PropTypes.string,
		labelFontSize         : PropTypes.string,
		disabled              : PropTypes.bool,
		avoidDebounce         : PropTypes.bool,
		labelAddition         : PropTypes.string,
	}),
};

TextInput.defaultProps = {
	// type        : 'text',
	value       : '',
	placeholder : '',
	rawErrors   : [],
	options     : {
		name                  : '',
		showPasswordStrength  : false,
		label                 : '',
		wrapperClassName      : '',
		inputClassName        : '',
		inputGroupClassName   : '',
		labelWrapperClassName : '',
		icon                  : '',
		componentClass        : 'input',
		textAreaRows          : 4,
		type                  : 'text',
		labelSubText          : '',
		large                 : true,
		labelFont             : 'font-regular',
		labelFontSize         : 'font-size-15',
		disabled              : false,
		avoidDebounce         : false,
		labelAddition         : '',
	},
};

export default TextInput;
